import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Copy, Check, Heart, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useDonation } from "@/context/DonationContext";
import { buildPixBRCode } from "@/lib/pix";
import { PIX_KEYS, PIX_RECEIVER, getPrimaryPixKey } from "@/lib/pixKeys";
import { analytics } from "@/lib/analytics";

export default function DonationModal() {
  const { open, closeModal } = useDonation();
  const [copiedId, setCopiedId] = useState(null);

  const primary = getPrimaryPixKey();
  const brcode = useMemo(
    () =>
      buildPixBRCode({
        key: primary.raw,
        name: PIX_RECEIVER.name,
        city: PIX_RECEIVER.city,
      }),
    [primary.raw]
  );

  const handleCopy = async (id, text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopiedId(id);
      toast.success("Copiado!");
      // Tracking (no-op if GA/Pixel not loaded)
      analytics.pixCopy(id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch {
      toast.error("Não foi possível copiar. Selecione manualmente.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeModal()}>
      <DialogContent
        data-testid="donation-modal"
        className="max-w-[92vw] sm:max-w-md md:max-w-lg rounded-3xl p-0 overflow-hidden border-0"
      >
        <div className="relative bg-gradient-to-br from-ga-emerald to-ga-emerald_deep text-white p-6 sm:p-8">
          <div className="absolute -top-20 -right-20 w-52 h-52 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-bold uppercase tracking-[0.2em] mb-4">
              <Heart className="w-3.5 h-3.5" /> Pix
            </div>
            <DialogHeader className="space-y-1 text-left">
              <DialogTitle className="font-display font-black text-3xl leading-tight tracking-tight text-white">
                Faça sua doação
              </DialogTitle>
              <DialogDescription className="text-white/85 text-base">
                Contribua com a transformação de vidas através do Instituto GA+.
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        <div className="p-6 sm:p-8 pt-7">
          {/* Intro — reforço emocional antes do QR */}
          <p
            data-testid="donation-intro"
            className="text-center text-sm text-ga-ink/70 mb-5 max-w-sm mx-auto leading-relaxed"
          >
            Sua contribuição ajuda a manter este projeto vivo e transformar
            vidas todos os dias.
          </p>

          {/* QR Code */}
          <div className="flex flex-col items-center">
            <div
              data-testid="donation-qrcode"
              className="rounded-2xl bg-white p-4 border-2 border-ga-ink/5 shadow-sm"
            >
              <QRCodeSVG
                value={brcode}
                size={208}
                level="M"
                bgColor="#FFFFFF"
                fgColor="#0F172A"
                includeMargin={false}
              />
            </div>
            <p className="mt-3 text-xs text-ga-ink/60 text-center max-w-xs">
              Abra o app do seu banco, escolha pagar via Pix e escaneie o QR Code.
            </p>
            <button
              type="button"
              onClick={() => handleCopy("brcode", brcode)}
              data-testid="donation-copy-brcode"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-ga-ink text-white px-5 py-2.5 text-sm font-bold hover:bg-ga-ink/90 transition-colors"
            >
              {copiedId === "brcode" ? (
                <><Check className="w-4 h-4" /> Código copiado</>
              ) : (
                <><Copy className="w-4 h-4" /> Copiar código Pix (copia e cola)</>
              )}
            </button>
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-ga-ink/50">
              ou use uma das chaves
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>

          {/* Keys list */}
          <ul className="space-y-2" data-testid="donation-keys-list">
            {PIX_KEYS.map((k) => (
              <li
                key={k.id}
                data-testid={`pix-key-${k.id}`}
                className={`flex items-center gap-3 rounded-2xl border p-3 sm:p-4 transition-colors ${
                  k.primary
                    ? "border-ga-emerald/40 bg-ga-emerald/5"
                    : "border-border bg-white"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-ga-ink/55">
                      {k.label}
                    </span>
                    {k.primary && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-ga-emerald text-white px-2 py-0.5 rounded-full">
                        <ShieldCheck className="w-3 h-3" /> Principal
                      </span>
                    )}
                  </div>
                  <div
                    className="mt-1 font-mono text-sm text-ga-ink truncate"
                    title={k.display}
                  >
                    {k.display}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(k.id, k.display)}
                  data-testid={`pix-key-copy-${k.id}`}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-ga-ink text-white px-3 py-1.5 text-xs font-bold hover:bg-ga-ink/90 transition-colors"
                  aria-label={`Copiar ${k.label}`}
                >
                  {copiedId === k.id ? (
                    <><Check className="w-3.5 h-3.5" /> Copiado</>
                  ) : (
                    <><Copy className="w-3.5 h-3.5" /> Copiar</>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <p className="mt-6 text-center text-xs text-ga-ink/55">
            Em caso de dúvida, fale com o Instituto pelo WhatsApp. Todas as
            doações são confirmadas por recibo.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
